import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { mdiHistory, mdiOpenInNew } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { useAssetTypes, usePropertyDefinitionsByCodes } from '../../hooks/useQueries'
import { getLocalizedName } from '../../lib/localization'
import { parseInput } from './inputs'

// What a derived value says about how it was computed (spec 2026-10-06-15):
// source_details carries derivation_uid, definition_digest, the inputs read
// and the matched row. Rows written before carry derivation_code only.

export interface DerivationProvenanceDerivation {
    uid: string
    code?: string
    name?: Record<string, string> | null
    definition_digest?: string
}

interface ViewProps {
    org: string
    sourceDetails: Record<string, any>
    /** The derivation as it stands now; undefined while loading or unreadable. */
    derivation?: DerivationProvenanceDerivation
    propertyLabel: (code: string) => string
    typeLabel: (code: string) => string
}

/** Formats one recorded input value for the badge. */
export function formatProvenanceValue(
    v: unknown,
    t: (key: string) => string,
    language: string,
): string | null {
    if (v === null || v === undefined) return null
    if (typeof v === 'boolean') return v ? t('detail.derivation_input_yes') : t('detail.derivation_input_no')
    // Integers print as is (a year is no 1,999); decimals take the locale's separator.
    if (typeof v === 'number') return Number.isInteger(v) ? String(v) : v.toLocaleString(language, { maximumFractionDigits: 6 })
    if (typeof v === 'string') return v
    if (Array.isArray(v)) return v.map(x => (typeof x === 'string' ? x : JSON.stringify(x))).join(', ')
    if (typeof v === 'object' && v && 'digest' in v && Object.keys(v).length === 1) {
        return t('detail.derivation_input_digest')
    }
    return JSON.stringify(v)
}

export function DerivationProvenanceView({ org, sourceDetails, derivation, propertyLabel, typeLabel }: ViewProps) {
    const { t, i18n } = useTranslation('assets')
    const code: string | undefined = sourceDetails.derivation_code
    const uid: string | undefined = sourceDetails.derivation_uid
    const inputs = sourceDetails.inputs as Record<string, unknown> | undefined
    const comment: string | undefined = sourceDetails.matched_rule_comment

    // A row written before provenance existed: the code, as before.
    if (!uid || !inputs) {
        return code ? (
            <p className="text-sm text-muted-foreground" data-testid="derivation-source-code">{code}</p>
        ) : null
    }

    const name = (derivation?.name && getLocalizedName(derivation.name, i18n.language)) || code || uid
    const outdated =
        !!derivation?.definition_digest &&
        !!sourceDetails.definition_digest &&
        derivation.definition_digest !== sourceDetails.definition_digest

    const paths = Object.keys(inputs).sort()

    return (
        <div className="space-y-2" data-testid="derivation-provenance">
            <Link
                to="/$org/derivations/$uid"
                params={{ org, uid }}
                className="flex items-center gap-1.5 text-sm text-primary hover:underline"
                data-testid="derivation-source-link"
            >
                <span className="truncate">{name}</span>
                <AppIcon path={mdiOpenInNew} size="xs" className="flex-shrink-0" />
            </Link>

            {comment && (
                <p className="text-xs text-muted-foreground" data-testid="derivation-source-matched-row">
                    {t('detail.derivation_matched_row', { comment })}
                </p>
            )}

            {paths.length > 0 && (
                <div>
                    <p className="text-xs font-medium text-muted-foreground">{t('detail.derivation_inputs_title')}</p>
                    <dl className="mt-1 space-y-0.5 text-xs" data-testid="derivation-source-inputs">
                        {paths.map(path => {
                            const parsed = parseInput(path)
                            const label = (parsed.kind === 'parent' || parsed.kind === 'children') && parsed.typeCode
                                ? t(parsed.kind === 'parent' ? 'detail.derivation_input_parent' : 'detail.derivation_input_children', {
                                    label: propertyLabel(parsed.code || path),
                                    type: typeLabel(parsed.typeCode),
                                })
                                : propertyLabel(parsed.code || path)
                            const value = formatProvenanceValue(inputs[path], t, i18n.language)
                            return (
                                <div key={path} className="flex justify-between gap-2" data-testid={`derivation-source-input-${path}`}>
                                    <dt className="text-muted-foreground truncate" title={path}>{label}</dt>
                                    <dd className="text-right font-medium break-all">
                                        {value ?? <span className="italic text-muted-foreground">{t('detail.derivation_input_absent')}</span>}
                                    </dd>
                                </div>
                            )
                        })}
                    </dl>
                </div>
            )}

            {outdated && (
                <p className="flex items-start gap-1 text-xs text-warning" data-testid="derivation-source-outdated">
                    <AppIcon path={mdiHistory} size="xs" className="mt-0.5 flex-shrink-0" />
                    <span>{t('detail.derivation_earlier_version')}</span>
                </p>
            )}
        </div>
    )
}

/**
 * The derivation part of a source badge. Mounted only while the popover is
 * open, so the derivation, labels and types are fetched on demand (and
 * shared through the query cache by every badge of the page).
 */
export function DerivationProvenance({ org, sourceDetails }: { org: string; sourceDetails: Record<string, any> }) {
    const { i18n } = useTranslation()
    const uid: string | undefined = sourceDetails.derivation_uid
    const inputs = sourceDetails.inputs as Record<string, unknown> | undefined
    const hasProvenance = !!uid && !!inputs

    const { data: derivation } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/derivations/{derivationUid}',
        { params: { path: { orgCode: org, derivationUid: uid || '' } } },
        { enabled: hasProvenance, retry: false },
    )

    const codes = useMemo(() => {
        if (!inputs) return []
        return Array.from(new Set(Object.keys(inputs).map(p => parseInput(p).code).filter((c): c is string => !!c))).sort()
    }, [inputs])
    const { data: propDefs = [] } = usePropertyDefinitionsByCodes(org, hasProvenance ? codes : [])
    const { data: assetTypes = [] } = useAssetTypes(org)

    const propertyLabel = (code: string) => {
        const pd = propDefs.find(p => p.code === code)
        return (pd?.name && getLocalizedName(pd.name as Record<string, string>, i18n.language)) || code
    }
    const typeLabel = (code: string) => {
        const at = assetTypes.find(a => a.code === code)
        return (at?.name && getLocalizedName(at.name as Record<string, string>, i18n.language)) || code
    }

    return (
        <DerivationProvenanceView
            org={org}
            sourceDetails={sourceDetails}
            derivation={derivation as DerivationProvenanceDerivation | undefined}
            propertyLabel={propertyLabel}
            typeLabel={typeLabel}
        />
    )
}
